import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  username: string;
  nombre?: string;
  apellido?: string;
  rol: 'admin' | 'usuario';
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (token: string, userData: User) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // Inicializar desde localStorage al montar
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUsername = localStorage.getItem('username');
    const storedNombre = localStorage.getItem('nombre');
    const storedApellido = localStorage.getItem('apellido');
    const storedRol = localStorage.getItem('rol') as 'admin' | 'usuario' | null;

    if (storedToken && storedUsername && storedRol) {
      setToken(storedToken);
      setUser({
        username: storedUsername,
        nombre: storedNombre || undefined,
        apellido: storedApellido || undefined,
        rol: storedRol,
      });
    }
  }, []);

  const login = (newToken: string, userData: User) => {
    setToken(newToken);
    setUser(userData);

    // Mantener compatibilidad con localStorage por ahora
    localStorage.setItem('token', newToken);
    localStorage.setItem('username', userData.username);
    if (userData.nombre) localStorage.setItem('nombre', userData.nombre);
    if (userData.apellido) localStorage.setItem('apellido', userData.apellido);
    localStorage.setItem('rol', userData.rol);
  };

  const logout = () => {
    setToken(null);
    setUser(null);

    // Limpiar localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('nombre');
    localStorage.removeItem('apellido');
    localStorage.removeItem('rol');
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);

      // Actualizar localStorage
      if (userData.nombre) localStorage.setItem('nombre', userData.nombre);
      if (userData.apellido) localStorage.setItem('apellido', userData.apellido);
      if (userData.rol) localStorage.setItem('rol', userData.rol);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
